import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiPaperclip, FiPlus } from "react-icons/fi";
import "./Quality.css";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import {
    MockBanner, PROCESS_STEPS, People, RCA_CATEGORIES, ReleasePill, ResultPill, STAGES, SevChip, SourceChip,
    StagePill, UNTRACED_REASONS, fmtDate, fmtShort, hasPerm, stageIndex, stageLabel,
} from "./qualityUi";
import useQualityMeta from "./useQualityMeta";
import TestCaseForm from "./TestCaseForm";

/**
 * One production bug, in the Sprint L3 workspace layout: the bug on the left
 * with tabs that walk the lifecycle, and the traceability chain on the right.
 *
 * Stage moves go through the API's advance endpoint, which refuses a move whose
 * precondition is missing (no trace, no RCA, no fix ticket…). The reason comes
 * back on every read as `next_blocker`, so the button explains itself before it
 * is pressed.
 */
const TABS = [
    { key: "details", label: "Details" },
    { key: "trace", label: "Trace" },
    { key: "rca", label: "Root Cause" },
    { key: "fix", label: "Fix" },
    { key: "report", label: "Report" },
];

export default function ProductionBugDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [bug, setBug] = useState(null);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState(location.state?.tab || "details");
    const [rail, setRail] = useState("trace");
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await qualityApi.getBug(id);
            setBug(res.data);
            setErr("");
        } catch (e) {
            setErr(e.message || "Could not load this bug.");
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Every mutation returns the full bug, so screens never re-fetch after one.
    const run = async (fn, okMsg) => {
        setBusy(true);
        try {
            const res = await fn();
            if (res.data) setBug(res.data);
            if (res.message || okMsg) showSuccessToast(res.message || okMsg);
            return res;
        } catch (e) {
            showErrorToast(e.message || "Something went wrong.");
            return null;
        } finally {
            setBusy(false);
        }
    };

    if (err) return <div className="qa-page"><MockBanner /><div className="qa-empty">{err}</div></div>;
    if (!bug) return <div className="qa-page"><MockBanner /><div className="qa-empty">Loading…</div></div>;

    const si = stageIndex(bug.stage);

    return (
        <div className="qa-page">
            <MockBanner />
            <div className="qa-topbar">
                <button className="qa-back" onClick={() => navigate("/quality/bugs")} aria-label="Back">←</button>
                <span className="qa-crumb">Production Bugs / <b>{bug.code}</b></span>
                <span className="qa-sp" />
                {hasPerm("Delete Ticket") && (confirmDelete ? (
                    <>
                        <span className="qa-hint" style={{ color: "var(--qa-red)" }}>Delete {bug.code}?</span>
                        <button className="qa-btn qa-btn--danger" disabled={busy} onClick={async () => {
                            // Not through run(): that would replace the bug with the delete response.
                            setBusy(true);
                            try {
                                const res = await qualityApi.deleteBug(bug.id);
                                showSuccessToast(res.message || `${bug.code} deleted.`);
                                navigate("/quality/bugs");
                            } catch (e) {
                                showErrorToast(e.message || "Could not delete the bug.");
                                setConfirmDelete(false);
                                setBusy(false);
                            }
                        }}>Delete {bug.code}</button>
                        <button className="qa-btn" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep it</button>
                    </>
                ) : (
                    <button className="qa-btn qa-btn--danger" onClick={() => setConfirmDelete(true)}>Delete</button>
                ))}
                {bug.next_stage ? (
                    <>
                        {bug.next_blocker && <span className="qa-hint">{bug.next_blocker}</span>}
                        <button
                            className="qa-btn qa-btn--primary"
                            disabled={!!bug.next_blocker || busy}
                            onClick={() => run(() => qualityApi.advanceBug(bug.id))}
                        >
                            Move to {bug.next_stage_label} →
                        </button>
                    </>
                ) : <span className="qa-pill qa-pill--success">Closed</span>}
            </div>

            <div className="qa-ws">
                <div className="qa-ws__main">
                    <div className="qa-hero">
                        <h2>{bug.title}</h2>
                        <div className="qa-meta">
                            <SevChip value={bug.severity} long />
                            <StagePill value={bug.stage} />
                            <SourceChip value={bug.source} />
                            <span className="qa-chip">{bug.module}</span>
                            <span className="qa-chip">{bug.org_name}</span>
                        </div>
                        <div className="qa-stepper">
                            {STAGES.map((s, k) => (
                                <div key={s.key} className={`qa-st${k < si ? " qa-st--done" : k === si ? " qa-st--cur" : ""}`}>
                                    <span className="qa-st__c">{k < si ? "✓" : k + 1}</span>
                                    {s.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="qa-tabs" style={{ padding: "0 12px" }}>
                        {TABS.map((t) => (
                            <button key={t.key} className={`qa-tab${tab === t.key ? " qa-tab--on" : ""}`} onClick={() => setTab(t.key)}>
                                {t.label}
                                {t.key === "rca" && bug.actions.length > 0 && <span className="qa-tab__badge">{bug.actions.length}</span>}
                            </button>
                        ))}
                    </div>

                    <div className="qa-body">
                        {tab === "details" && <DetailsTab bug={bug} run={run} busy={busy} />}
                        {tab === "trace" && <TraceTab bug={bug} run={run} busy={busy} />}
                        {tab === "rca" && <RcaTab bug={bug} run={run} busy={busy} onGoTrace={() => setTab("trace")} reload={load} />}
                        {tab === "fix" && <FixTab bug={bug} run={run} busy={busy} />}
                        {tab === "report" && <ReportTab bug={bug} />}
                    </div>
                </div>

                <div className="qa-ws__rail">
                    <div className="qa-tabs">
                        <button className={`qa-tab${rail === "trace" ? " qa-tab--on" : ""}`} onClick={() => setRail("trace")}>Traceability</button>
                        <button className={`qa-tab${rail === "log" ? " qa-tab--on" : ""}`} onClick={() => setRail("log")}>Timeline</button>
                    </div>
                    <div className="qa-rail-body">
                        {rail === "trace" ? <Chain bug={bug} /> : <Timeline bug={bug} />}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Details ─────────────────────────────────────────────────────────── */
function DetailsTab({ bug, run, busy }) {
    const canEdit = hasPerm("Log Production Bug");
    const Text = ({ label, value }) => (
        <div className="qa-sec">
            <h4>{label}</h4>
            <p style={{ margin: 0, fontSize: ".82rem", maxWidth: "75ch", whiteSpace: "pre-wrap" }}>
                {value || <span className="qa-faint">Not recorded.</span>}
            </p>
        </div>
    );
    const open = async (a) => {
        try {
            const res = await qualityApi.getAttachmentUrl(a.id);
            window.open(res.data.url, "_blank", "noopener");
        } catch (e) {
            showErrorToast(e.message || "Could not open the file.");
        }
    };
    const upload = (list) => {
        if (list && list.length) run(() => qualityApi.uploadAttachments(bug.id, list));
    };
    const num = (n) => (n == null ? "—" : n);
    return (
        <>
            {bug.patient_safety && (
                <div className="qa-banner qa-banner--bad">
                    <span className="qa-banner__tx"><b>Patient safety or data integrity affected.</b></span>
                </div>
            )}
            <div className="qa-sec">
                <h4>When &amp; who</h4>
                <dl className="qa-grid">
                    <div><dt>Happened</dt><dd>{fmtDate(bug.occurred_at || bug.reported_at, true)}</dd></div>
                    <div><dt>Reported at hospital by</dt><dd>{bug.hospital_reporter || "—"}</dd></div>
                    <div><dt>Logged</dt><dd>{fmtDate(bug.reported_at, true)}</dd></div>
                    <div><dt>Logged by</dt><dd>{bug.reported_by}</dd></div>
                </dl>
            </div>
            <div className="qa-sec">
                <h4>Where</h4>
                <dl className="qa-grid">
                    <div><dt>Hospital</dt><dd>{bug.org_name || "—"}</dd></div>
                    <div><dt>Server</dt><dd>{bug.server_label || "—"}</dd></div>
                    <div><dt>Module · screen</dt><dd>{bug.module}{bug.sub_module ? ` · ${bug.sub_module}` : ""}</dd></div>
                    <div><dt>App version</dt><dd>{bug.app_version || "—"}</dd></div>
                    <div><dt>Screen URL</dt><dd className="qa-mono" style={{ fontSize: ".74rem" }}>{bug.screen_url || "—"}</dd></div>
                    <div><dt>Browser / device</dt><dd>{bug.browser_device || "—"}</dd></div>
                </dl>
            </div>
            <div className="qa-dgrid">
                <div><Text label="Expected result" value={bug.expected_result} /></div>
                <div><Text label="Actual result" value={bug.actual_result} /></div>
            </div>
            <Text label="Steps to reproduce" value={bug.steps_to_reproduce} />
            {bug.details && <Text label="Other details" value={bug.details} />}
            <div className="qa-sec">
                <h4>Impact</h4>
                <dl className="qa-grid">
                    <div><dt>Users affected</dt><dd>{num(bug.users_affected)}</dd></div>
                    <div><dt>Hospitals affected</dt><dd>{num(bug.hospitals_affected)}</dd></div>
                    <div><dt>Patient safety / data</dt><dd>{bug.patient_safety ? "Yes" : "No"}</dd></div>
                </dl>
            </div>
            <div className="qa-sec">
                <h4>Evidence</h4>
                {(bug.attachments || []).length === 0 && <p className="qa-hint" style={{ margin: "0 0 10px" }}>No files attached.</p>}
                {(bug.attachments || []).length > 0 && (
                    <ul className="qa-files" style={{ marginTop: 0, marginBottom: 10 }}>
                        {bug.attachments.map((a) => (
                            <li key={a.id}>
                                <span>{a.file_name}</span>
                                <span className="qa-hint">{Math.max(1, Math.round((a.size_bytes || 0) / 1024))} KB · {a.uploaded_by}</span>
                                <button type="button" className="qa-linkbtn" onClick={() => open(a)}>Open</button>
                                {canEdit && (
                                    <button type="button" className="qa-x" disabled={busy} aria-label={`Remove ${a.file_name}`}
                                        onClick={() => run(() => qualityApi.deleteAttachment(a.id))}>×</button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {canEdit && (
                    <label className="qa-drop">
                        <input id="qa-bug-files" type="file" multiple disabled={busy}
                            onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
                        <FiPaperclip size={14} /> Attach files
                    </label>
                )}
            </div>
            <div className="qa-sec">
                <h4>Linked source</h4>
                <div className="qa-box" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <SourceChip value={bug.source} />
                    <span className="qa-mono" style={{ fontSize: ".78rem" }}>{bug.source_ref}</span>
                </div>
            </div>
        </>
    );
}

/* ── Trace ───────────────────────────────────────────────────────────── */
function TraceTab({ bug, run, busy }) {
    const [sugs, setSugs] = useState(null);

    useEffect(() => {
        if (bug.release || bug.untraced_reason) return;
        qualityApi.getSuggestions(bug.id).then((res) => setSugs(res.data || [])).catch(() => setSugs([]));
    }, [bug.id, bug.release, bug.untraced_reason]);

    const canEdit = hasPerm("Log Production Bug");

    if (bug.release && bug.ticket) {
        const t = bug.ticket;
        return (
            <>
                <div className="qa-banner qa-banner--ok">
                    <span className="qa-banner__tx">
                        Traced to <b>{bug.release.version}</b> · <b>{t.code}</b> ({t.sprint}). People and results below are the
                        snapshot taken when the ticket went into that release.
                    </span>
                    {canEdit && <button className="qa-btn qa-btn--sm" disabled={busy} onClick={() => run(() => qualityApi.clearTrace(bug.id))}>Change</button>}
                </div>
                <div className="qa-sec">
                    <h4>Test cases on {t.code} in {bug.release.version}</h4>
                    <div className="qa-card qa-card--solo">
                        <table className="qa-tbl">
                            <thead><tr><th>Test case</th><th>Title</th><th>Before release</th><th>Tester</th><th>Date</th></tr></thead>
                            <tbody>
                                {t.test_cases.length === 0 && (
                                    <tr><td colSpan={5} className="qa-empty">This ticket had no test cases linked when it was released.</td></tr>
                                )}
                                {t.test_cases.map((c) => (
                                    <tr key={c.code}>
                                        <td className="qa-code qa-mono">{c.code}</td>
                                        <td>{c.title}</td>
                                        <td>{c.result ? <ResultPill value={c.result} /> : <span className="qa-pill qa-pill--danger">Not in run</span>}</td>
                                        <td>{c.tester || "—"}</td>
                                        <td className="qa-nw qa-muted">{fmtShort(c.executed_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }

    if (bug.untraced_reason) {
        return (
            <div className="qa-banner qa-banner--warn">
                <span className="qa-banner__tx">
                    Marked untraced: <b>{bug.untraced_reason}</b>. No release introduced this bug, so the root cause will focus on
                    environment and process.
                </span>
                {canEdit && <button className="qa-btn qa-btn--sm" disabled={busy} onClick={() => run(() => qualityApi.clearTrace(bug.id))}>Change</button>}
            </div>
        );
    }

    return (
        <>
            <div className="qa-banner qa-banner--info">
                <span className="qa-banner__tx">
                    Releases deployed before <b>{fmtDate(bug.reported_at)}</b> that changed <b>{bug.module}</b>, newest first.
                    Confirm the ticket that introduced the bug.
                </span>
            </div>
            {sugs === null && <div className="qa-empty">Looking for candidate releases…</div>}
            {sugs && sugs.length === 0 && (
                <div className="qa-banner qa-banner--warn">
                    <span className="qa-banner__tx">No deployed release changed {bug.module} before this bug was reported.</span>
                </div>
            )}
            {(sugs || []).map((s, k) => (
                <div className="qa-sug" key={s.release.id}>
                    <div className="qa-sug__h">
                        <span className="qa-mono" style={{ fontWeight: 700, color: "var(--text-primary)" }}>{s.release.version}</span>
                        <ReleasePill value={s.release.status} />
                        <span className="qa-hint">Deployed {fmtDate(s.release.release_date)}</span>
                        {k === 0 && <span className="qa-chip qa-chip--accent">Most recent</span>}
                    </div>
                    {s.tickets.map((t) => (
                        <div className="qa-sug__r" key={t.code}>
                            <span className="qa-code">{t.code}</span>
                            <span className="qa-sug__d">
                                {t.description}
                                <div className="qa-hint">{t.developers.join(", ")} · QA {t.qa} · {t.sprint}</div>
                            </span>
                            {canEdit && (
                                <button
                                    className="qa-btn qa-btn--sm qa-btn--primary" disabled={busy}
                                    onClick={() => run(() => qualityApi.traceBug(bug.id, { release_id: s.release.id, ticket_code: t.code }))}
                                >
                                    Confirm as cause
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            ))}
            {canEdit && (
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                    <span className="qa-hint">Not caused by a release?</span>
                    <select
                        id="qa-untraced" className="qa-filter" value="" disabled={busy}
                        onChange={(e) => e.target.value && run(() => qualityApi.traceBug(bug.id, { untraced_reason: e.target.value }))}
                    >
                        <option value="">Mark untraced as…</option>
                        {UNTRACED_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
            )}
        </>
    );
}

/* ── Root cause + corrective/preventive actions ─────────────────────── */
function RcaTab({ bug, run, busy, onGoTrace, reload }) {
    const meta = useQualityMeta();
    const [cases, setCases] = useState([]);
    const [execs, setExecs] = useState([]);
    const [f, setF] = useState(() => ({
        missed_test_case: bug.rca?.missed_test_case || "",
        category: bug.rca?.category || "",
        process_step: bug.rca?.process_step || "",
        why_not_detected: bug.rca?.why_not_detected || "",
        root_cause: bug.rca?.root_cause || "",
        contributing_factors: bug.rca?.contributing_factors || "",
    }));
    const [action, setAction] = useState({ type: "Preventive", description: "", owner: "", due_date: "" });
    const [tcFor, setTcFor] = useState(null);

    useEffect(() => {
        qualityApi.getTestCases({ module: bug.module }).then((res) => setCases(res.data || [])).catch(() => setCases([]));
        if (bug.release) {
            qualityApi.getRelease(bug.release.id).then((res) => setExecs(res.data.executions || [])).catch(() => setExecs([]));
        }
    }, [bug.module, bug.release]);

    if (!bug.release && !bug.untraced_reason) {
        return (
            <div className="qa-banner qa-banner--warn">
                <span className="qa-banner__tx">Trace the bug first, so the analysis can show who worked on the change and which tests ran.</span>
                <button className="qa-btn qa-btn--sm" onClick={onGoTrace}>Go to Trace</button>
            </div>
        );
    }

    const canEdit = hasPerm("Log Production Bug") || hasPerm("Approve RCA");
    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
    const exec = execs.find((e) => e.tc_code === f.missed_test_case);

    let fact;
    if (!f.missed_test_case) fact = <>No test case covered this. Add a preventive action to create one.</>;
    else if (!bug.release) fact = <>The bug is untraced, so there is no release run to check.</>;
    else if (!exec) fact = <><b>{f.missed_test_case}</b> was not in the {bug.release.version} regression run.</>;
    else if (exec.result === "not_run") fact = <><b>{f.missed_test_case}</b> was in the {bug.release.version} run but was <b>never executed</b>.</>;
    else {
        fact = (
            <>
                <b>{f.missed_test_case}</b> ran before {bug.release.version}: <b>{exec.result.toUpperCase()}</b> by {exec.tester} on {fmtDate(exec.executed_at)}.
                {exec.result === "pass" && " It passed, so its steps need review."}
            </>
        );
    }

    const addAction = async () => {
        const res = await run(() => qualityApi.addAction(bug.id, action));
        if (res) setAction({ type: action.type, description: "", owner: "", due_date: "" });
    };

    // Pre-fill the test case form from the bug, so the missing test is written
    // against the scenario that actually escaped.
    const openTestCase = (a) => {
        const title = a.description.replace(/^add (a )?(new )?(test case|test)( for)?:?\s*/i, "");
        setTcFor({
            action: a,
            prefill: {
                title: title.charAt(0).toUpperCase() + title.slice(1),
                module: bug.module,
                case_type: /boundary|window|limit|missing/i.test(`${bug.rca?.category} ${bug.rca?.why_not_detected}`) ? "Boundary" : "Negative",
                priority: bug.severity === "P0" || bug.severity === "P1" ? "High" : "Medium",
                critical: bug.severity === "P0",
                preconditions: `Scenario from ${bug.code} (${bug.org_name}): ${bug.details}`,
                owner: a.owner,
                tags: "from-production-bug",
            },
        });
    };

    return (
        <>
            <div className="qa-sec">
                <h4>Root cause analysis</h4>
                <div className="qa-form">
                    <label className="qa-fl qa-full">
                        Test case that should have caught it
                        <select id="qa-rca-missed" className="qa-input" value={f.missed_test_case} disabled={!canEdit} onChange={(e) => set({ missed_test_case: e.target.value })}>
                            <option value="">None. No test case exists</option>
                            {cases.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.title}</option>)}
                        </select>
                    </label>
                    <div className="qa-full qa-auto">{fact}</div>
                    <label className="qa-fl">
                        <span className="qa-req">Root cause category</span>
                        <select id="qa-rca-cat" className="qa-input" value={f.category} disabled={!canEdit} onChange={(e) => set({ category: e.target.value })}>
                            <option value="">Choose…</option>
                            {RCA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                    <label className="qa-fl">
                        Step that let it through
                        <select id="qa-rca-step" className="qa-input" value={f.process_step} disabled={!canEdit} onChange={(e) => set({ process_step: e.target.value })}>
                            <option value="">Choose…</option>
                            {PROCESS_STEPS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                    <label className="qa-fl qa-full">
                        Why wasn't it caught in testing?
                        <textarea id="qa-rca-why" className="qa-input" value={f.why_not_detected} disabled={!canEdit} onChange={(e) => set({ why_not_detected: e.target.value })} />
                    </label>
                    <label className="qa-fl qa-full">
                        <span className="qa-req">Root cause</span>
                        <textarea id="qa-rca-root" className="qa-input" value={f.root_cause} disabled={!canEdit} onChange={(e) => set({ root_cause: e.target.value })} />
                    </label>
                    <label className="qa-fl qa-full">
                        Contributing factors
                        <textarea id="qa-rca-contrib" className="qa-input" value={f.contributing_factors} disabled={!canEdit} onChange={(e) => set({ contributing_factors: e.target.value })} />
                    </label>
                </div>
                {canEdit && (
                    <div style={{ marginTop: 12 }}>
                        <button className="qa-btn qa-btn--primary" disabled={busy} onClick={() => run(() => qualityApi.saveRca(bug.id, f))}>
                            Save analysis
                        </button>
                    </div>
                )}
            </div>

            <div className="qa-sec">
                <h4>Corrective &amp; preventive actions</h4>
                <div className="qa-card qa-card--solo">
                    <table className="qa-tbl">
                        <thead><tr><th>Type</th><th>Action</th><th>Owner</th><th>Due</th><th>Status</th><th>Test case</th></tr></thead>
                        <tbody>
                            {bug.actions.length === 0 && <tr><td colSpan={6} className="qa-empty">No actions yet.</td></tr>}
                            {bug.actions.map((a) => (
                                <tr key={a.id}>
                                    <td><span className={`qa-chip ${a.type === "Corrective" ? "qa-chip--blue" : "qa-chip--green"}`}>{a.type}</span></td>
                                    <td>{a.description}</td>
                                    <td><People names={[a.owner]} /></td>
                                    <td className="qa-nw">{fmtShort(a.due_date)}</td>
                                    <td>{a.status}</td>
                                    <td className="qa-nw">
                                        {a.test_case
                                            ? <span className="qa-chip qa-chip--accent qa-mono">{a.test_case}</span>
                                            : a.type === "Preventive" && hasPerm("Manage Test Cases")
                                                ? <button className="qa-btn qa-btn--sm qa-btn--primary" onClick={() => openTestCase(a)}><FiPlus size={11} /> Create test case</button>
                                                : <span className="qa-faint">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {canEdit && (
                    <div className="qa-form" style={{ marginTop: 12 }}>
                        <label className="qa-fl">
                            Type
                            <select id="qa-act-type" className="qa-input" value={action.type} onChange={(e) => setAction({ ...action, type: e.target.value })}>
                                <option value="Corrective">Corrective · fix this bug</option>
                                <option value="Preventive">Preventive · stop it happening again</option>
                            </select>
                        </label>
                        <label className="qa-fl">
                            Owner
                            <select id="qa-act-owner" className="qa-input" value={action.owner} onChange={(e) => setAction({ ...action, owner: e.target.value })}>
                                <option value="">Choose…</option>
                                {meta.members.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl qa-full">
                            Action
                            <input
                                id="qa-act-desc" className="qa-input" value={action.description}
                                onChange={(e) => setAction({ ...action, description: e.target.value })}
                                placeholder="e.g. Add a test case for partial sales returns"
                            />
                        </label>
                        <label className="qa-fl">
                            Due date
                            <input id="qa-act-due" type="date" className="qa-input" value={action.due_date} onChange={(e) => setAction({ ...action, due_date: e.target.value })} />
                        </label>
                        <div className="qa-fl" style={{ justifyContent: "flex-end" }}>
                            <button className="qa-btn" disabled={busy} onClick={addAction}><FiPlus size={12} /> Add action</button>
                        </div>
                    </div>
                )}
                <p className="qa-hint" style={{ margin: "10px 0 0" }}>
                    Use <b>Create test case</b> on a preventive action to write the missing test. It opens pre-filled from this
                    bug, and once saved it joins the next regression run.
                </p>
            </div>

            {tcFor && (
                <TestCaseForm
                    prefill={tcFor.prefill}
                    context={{ bugId: bug.id, bugCode: bug.code, actionId: tcFor.action.id }}
                    onClose={() => setTcFor(null)}
                    onSaved={() => { setTcFor(null); reload(); }}
                />
            )}
        </>
    );
}

/* ── Fix ─────────────────────────────────────────────────────────────── */
function FixTab({ bug, run, busy }) {
    const navigate = useNavigate();
    const meta = useQualityMeta();
    const [sprintId, setSprintId] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const canEdit = hasPerm("Log Production Bug");
    const canDelete = hasPerm("Delete Ticket");
    const fx = bug.fix_ticket;

    // Default to the first open sprint: live-support sprints come first.
    useEffect(() => {
        if (!sprintId && meta.sprints.length) setSprintId(String(meta.sprints[0].id));
    }, [meta.sprints, sprintId]);

    // Opens the ticket itself on the Sprint screen, in its own sprint.
    const openTicket = () => navigate(`/sprints?sprint=${fx.sprint_id}&ticket=${fx.id}`);

    if (fx) {
        return (
            <>
                <div className="qa-sec">
                    <h4>Fix ticket</h4>
                    <dl className="qa-grid">
                        <div><dt>Ticket</dt><dd className="qa-code">{fx.code}</dd></div>
                        <div><dt>Type</dt><dd>Bug · Production</dd></div>
                        <div><dt>Parent</dt><dd>{fx.parent || bug.ticket_code || "—"}</dd></div>
                        <div><dt>Sprint</dt><dd>{fx.sprint || "—"}</dd></div>
                        <div>
                            <dt>Status</dt>
                            <dd>
                                {canEdit && meta.ticket_statuses.length ? (
                                    <select
                                        id="qa-fix-status" className="qa-filter" value={fx.status_id || ""} disabled={busy}
                                        aria-label="Fix ticket status"
                                        onChange={(e) => run(() => qualityApi.setFixTicketStatus(bug.id, e.target.value))}
                                    >
                                        {!fx.status_id && <option value="">—</option>}
                                        {meta.ticket_statuses.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                                    </select>
                                ) : <span className="qa-pill qa-pill--info">{fx.status || "—"}</span>}
                            </dd>
                        </div>
                    </dl>
                </div>
                <div className="qa-banner qa-banner--info" style={{ marginTop: 16 }}>
                    <span className="qa-banner__tx">
                        The fix follows the normal sprint flow: dev, QA, UAT, then a Live Move. Status changes here are the same as
                        on the Sprint screen, so Production still needs an approved Live Move. It reaches hospitals only after the
                        next release passes its regression run.
                    </span>
                    {fx.sprint_id && <button className="qa-btn qa-btn--sm" onClick={openTicket}>Open {fx.code} in {fx.sprint}</button>}
                </div>
                {canDelete && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
                        {confirmDelete ? (
                            <>
                                <span className="qa-hint" style={{ color: "var(--qa-red)" }}>
                                    Delete {fx.code} from {fx.sprint}? You can create a new fix ticket afterwards.
                                </span>
                                <button className="qa-btn qa-btn--sm qa-btn--danger" disabled={busy}
                                    onClick={async () => { await run(() => qualityApi.deleteFixTicket(bug.id)); setConfirmDelete(false); }}>
                                    {busy ? "Deleting…" : `Delete ${fx.code}`}
                                </button>
                                <button className="qa-btn qa-btn--sm" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep it</button>
                            </>
                        ) : (
                            <button className="qa-btn qa-btn--sm qa-btn--danger" onClick={() => setConfirmDelete(true)}>
                                Delete fix ticket
                            </button>
                        )}
                    </div>
                )}
            </>
        );
    }
    if (!bug.rca) {
        return <div className="qa-banner qa-banner--warn"><span className="qa-banner__tx">Save the root cause analysis before creating the fix ticket.</span></div>;
    }
    return (
        <div className="qa-sec">
            {bug.deleted_fix_ticket && (
                <div className="qa-banner qa-banner--warn">
                    <span className="qa-banner__tx">
                        The fix ticket <b>{bug.deleted_fix_ticket}</b> was deleted. Create a new one below.
                    </span>
                </div>
            )}
            <h4>Create fix ticket</h4>
            <p className="qa-muted" style={{ marginTop: 0, fontSize: ".8rem" }}>
                Creates a <b>Bug · Production</b> ticket {bug.ticket_code ? <>under <b>{bug.ticket_code}</b></> : "as a new ticket"} in the
                sprint you choose, through the normal ticket flow.
            </p>
            {meta.sprints.length === 0 ? (
                <div className="qa-banner qa-banner--warn">
                    <span className="qa-banner__tx">There is no ongoing sprint to put the fix in. Start one in Sprints first.</span>
                </div>
            ) : canEdit && (
                <div className="qa-form" style={{ alignItems: "end" }}>
                    <label className="qa-fl">
                        Sprint
                        <select id="qa-fix-sprint" className="qa-input" value={sprintId} onChange={(e) => setSprintId(e.target.value)}>
                            {meta.sprints.map((sp) => (
                                <option key={sp.id} value={sp.id}>{sp.name}{sp.is_live_support ? " · live support" : ""}</option>
                            ))}
                        </select>
                    </label>
                    <div>
                        <button className="qa-btn qa-btn--primary" disabled={busy || !sprintId}
                            onClick={() => run(() => qualityApi.createFixTicket(bug.id, sprintId))}>
                            Create fix ticket
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Report ──────────────────────────────────────────────────────────── */
function ReportTab({ bug }) {
    const t = bug.ticket;
    const r = bug.rca;
    const x = r?.missed_execution;
    const Q = ({ k, children }) => (
        <div className="qa-q">
            <div className="qa-q__k">{k}</div>
            <div className="qa-q__v">{children || <span className="qa-faint">Not recorded yet</span>}</div>
        </div>
    );
    const preventive = bug.actions.filter((a) => a.type === "Preventive");
    return (
        <>
            <div className="qa-sec">
                <h4>Bug &amp; release</h4>
                <dl className="qa-grid">
                    <div><dt>Bug</dt><dd>{bug.code}</dd></div>
                    <div><dt>Severity</dt><dd><SevChip value={bug.severity} /></dd></div>
                    <div><dt>Hospital</dt><dd>{bug.org_name}</dd></div>
                    <div><dt>Module</dt><dd>{bug.module}</dd></div>
                    <div><dt>Release</dt><dd>{bug.release ? <><span className="qa-mono">{bug.release.version}</span> · {fmtDate(bug.release.release_date)}</> : "—"}</dd></div>
                    <div><dt>Sprint · ticket</dt><dd>{t ? `${t.sprint} · ${t.code}` : "—"}</dd></div>
                </dl>
            </div>
            <div className="qa-sec">
                <h4>People &amp; tests</h4>
                <dl className="qa-grid">
                    <div><dt>Developer</dt><dd>{t ? t.developers.join(", ") : "—"}</dd></div>
                    <div><dt>Approved by</dt><dd>{t ? t.approver || "—" : "—"}</dd></div>
                    <div><dt>QA · UAT</dt><dd>{t ? `${t.qa} · ${t.uat}` : "—"}</dd></div>
                    <div><dt>Code · reviewer</dt><dd className="qa-faint">Not tracked yet</dd></div>
                </dl>
            </div>
            <div className="qa-sec">
                <h4>The management questions</h4>
                <Q k="How did this reach production?">{r?.why_not_detected}</Q>
                <Q k="Which change introduced it?">{t ? `${t.code} in ${bug.release.version}` : bug.untraced_reason ? `Not a release change (${bug.untraced_reason})` : ""}</Q>
                <Q k="Which test should have caught it?">{r ? (r.missed_test_case ? `${r.missed_test_case} · ${r.missed_test_case_title}` : "No test existed") : ""}</Q>
                <Q k="Was it run? Why did it pass?">{r ? (x ? `${x.result.toUpperCase()} by ${x.tester || "—"} on ${fmtDate(x.executed_at)}` : r.missed_test_case ? "Not in the release run" : "—") : ""}</Q>
                <Q k="Root cause">{r ? `${r.category}: ${r.root_cause}` : ""}</Q>
                <Q k="What stops it happening again?">
                    {preventive.length ? preventive.map((a) => (
                        <div key={a.id}>{a.description} ({a.owner || "no owner"}, {fmtShort(a.due_date)}){a.test_case ? ` · ${a.test_case}` : ""}</div>
                    )) : null}
                </Q>
            </div>
        </>
    );
}

/* ── right rail ──────────────────────────────────────────────────────── */
function Chain({ bug }) {
    const t = bug.ticket;
    const Item = ({ label, children }) => (
        <li className={children ? "" : "qa-chain--empty"}>
            <span className="qa-chain__n" />
            <div>
                <div className="qa-chain__l">{label}</div>
                <div className="qa-chain__v">{children || "Not traced yet"}</div>
            </div>
        </li>
    );
    return (
        <ul className="qa-chain">
            <Item label="Production bug"><b>{bug.code}</b> · {bug.org_name}</Item>
            <Item label="Release">{bug.release && <><span className="qa-mono">{bug.release.version}</span> · {fmtDate(bug.release.release_date)}</>}</Item>
            <Item label="Sprint">{t && t.sprint}</Item>
            <Item label="Ticket">{t && <><b>{t.code}</b> {t.description}</>}</Item>
            <Item label="Developer">{t && t.developers.join(", ")}</Item>
            <Item label="Code change · reviewer"><span className="qa-faint">Not tracked yet</span></Item>
            <Item label="Approved by">{t && <>{t.approver || "—"} <span className="qa-faint">(Live Move)</span></>}</Item>
            <Item label="Testers">{t && `QA ${t.qa}${t.peer_qa ? ` · Peer ${t.peer_qa}` : ""} · UAT ${t.uat}`}</Item>
            <Item label="Test cases">
                {t && t.test_cases.length > 0 && t.test_cases.map((c) => (
                    <div key={c.code}><span className="qa-mono">{c.code}</span>{" "}
                        {c.result ? <ResultPill value={c.result} /> : <span className="qa-pill qa-pill--danger">Not in run</span>}
                    </div>
                ))}
            </Item>
            <Item label="Root cause">{bug.rca && bug.rca.category}</Item>
            <Item label="Actions">{bug.actions.length > 0 && `${bug.actions.length} corrective / preventive`}</Item>
        </ul>
    );
}

function Timeline({ bug }) {
    return (
        <div className="qa-log">
            {bug.stage_log.slice().reverse().map((x, i) => (
                <div key={`${x.stage}-${i}`}>
                    <b>{stageLabel(x.stage)}</b><br />
                    <span className="qa-hint">{x.by} · {fmtDate(x.at, true)}</span>
                </div>
            ))}
        </div>
    );
}
